"use client";

import Image from "next/image";
import Script from "next/script";
import { Layout } from "@/components/Layout/Layout";
import { HeroBanner } from "@/components/HeroBanner/HeroBanner";
import { Timeline } from "@/components/Timeline/Timeline";
import { Heading } from "@/components/Heading/Heading";
import { serialize } from "@/app/utils/generateHTML";
import { getImageSrc } from "@/app/utils/getImageSrc";
import HTMLReactParser from "html-react-parser";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

const businessJsonLd = {
	"@context": "https://schema.org",
	"@type": "Organization",
	"name": "Hindustan Power | Business",
	"url": `https://stg.hindustanpower.in/business/thermal-power` || `https://stg.hindustanpower.in/business/renewable` || `https://stg.hindustanpower.in/business/mining`,
	"logo": "https://stg.hindustanpower.in/logo.png",
	"keywords": [
		"Hindustan Power",
		"Energy",
		"Business",
		"Power Projects",
		"Renewable Energy",
		"Infrastructure",
		"India",
	],
	"description": "Hindustan Power is a leading energy company in India, specializing in power projects, renewable energy, and infrastructure development.",
}

export default function Wrapper({ pageData }) {
	return (
		<Layout>
			<Script id="schema-markup" type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(businessJsonLd), }} />
			<HeroBanner image={getImageSrc(pageData.image.url)} title={pageData.basic.title} about={HTMLReactParser(pageData.basic.text)} mobileImage={getImageSrc(pageData.mobileImage.url)} alt={pageData.image?.alt || String(pageData.basic.title).replace(/<[^>]*>/g, "")} mobileAlt={pageData.mobileImage?.alt || String(pageData.basic.title).replace(/<[^>]*>/g, "")} />
			<section className="py-8 lg:py-16">
				<div className="hp-container flex flex-col items-center">
					<div className="text-center flex flex-col gap-4 mb-5 md:max-w-[80%] xl:max-w-[65%]">
						<Heading>{HTMLReactParser(pageData.basic.introTitle)}</Heading>
						{serialize(pageData.basic.introText)}
					</div>
					<div className=" flex-wrap justify-between xl:justify-center gap-5 xl:gap-12 py-8 w-full hidden lg:flex">
						{pageData.features.features.map((feature, key) => {
							return (
								<div className="flex flex-col gap-2 justify-center items-start py-12 px-12 rounded-[15px] bg-blue w-full md:w-[30.33%] 2xl:w-[31%] group hover:bg-primary hover:text-white" key={key}>
									<Image alt={feature.image?.alt || feature.title} src={getImageSrc(feature.image.url)} width={40} height={40} unoptimized className="block group-hover:hidden" />
									<Image alt={feature.hoverImage?.alt || feature.title} src={getImageSrc(feature.hoverImage.url)} width={40} height={40} unoptimized className="hidden group-hover:block" />
									<h4 className="text-[50px] font-medium leading-[60px]">{feature.title}</h4>
									<div className="h-[1px] bg-red w-full my-1 group-hover:bg-white"></div>
									<p className="text-[18px]  font-normal">{feature.text}</p>
								</div>
							);
						})}
					</div>

					<div className="w-full beneficiary-slider block lg:hidden mt-5">
						<Swiper loop={true} pagination={{ clickable: true }} modules={[Pagination]} spaceBetween={15} slidesPerView={1.15} breakpoints={{ 640: { slidesPerView: 2.1, }, 1024: { slidesPerView: 3.1, }, }}>
							{pageData.features.features.map((feature, key) => {
								return (
									<SwiperSlide key={key}>
										<div className="flex flex-col gap-2 justify-center items-start py-12 px-12 rounded-[15px] bg-blue w-full lg:w-[30.33%] 2xl:w-[31%] h-full">
											<Image alt={feature.image?.alt || feature.title} src={getImageSrc(feature.image.url)} width={40} height={40} unoptimized className="block group-hover:hidden" />
											<Image alt={feature.hoverImage?.alt || feature.title} src={getImageSrc(feature.hoverImage.url)} width={40} height={40} unoptimized className="hidden group-hover:block" />
											<h4 className="text-[50px] font-medium leading-[60px]">{feature.title}</h4>
											<div className="h-[1px] bg-red w-full my-1"></div>
											<p className="text-[18px]  font-normal">{feature.text}</p>
										</div>
									</SwiperSlide>
								);
							})}
						</Swiper>
					</div>
				</div>
			</section>

			{pageData.basic.timeline ? (<Timeline timeline={pageData.basic.timeline} />) : (<></>)}
			{pageData.projects.projects.length > 0 ? (
				<section className="py-8 lg:py-16 relative z-40">
					<div className="hp-container flex flex-col items-center">
						<div className="text-center flex flex-col gap-4 mb-5 lg:max-w-[60%]">
							<Heading>Our Projects</Heading>
						</div>
						<div className="w-full beneficiary-slider mt-5">
							<Swiper loop={true} pagination={{ clickable: true }} modules={[Pagination]} spaceBetween={15} slidesPerView={1.3} breakpoints={{ 576: { slidesPerView: 3.4, }, 1024: { spaceBetween: 35, slidesPerView: 4.4, }, 1280: { spaceBetween: 35, slidesPerView: 5.4, }, }}>
								{pageData.projects.projects.map(
									(project, key) => {
										return (
											<SwiperSlide key={key}>
												<div className="flex flex-col gap-2 justify-center items-start w-full">
													<Image alt={project.image?.alt || project.title} src={getImageSrc(project.image.url)} width={40} height={40} unoptimized className="w-full" />
													<h4 className="text-[14px] lg:text-[16px] xl:text-[18px] font-medium leading-[24px] lg:leading-[28px]">{project.title}</h4>
												</div>
											</SwiperSlide>
										);
									}
								)}
							</Swiper>
						</div>
					</div>
				</section>
			) : (<></>)}
		</Layout>
	);
}
